import React, { useCallback, useMemo, useState } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import styles from './styles.module.css';
import { INavItem, ITagFilterItem } from 'constants/types';
import { SelectedOrdinaryFilters } from 'components/template/collection-casino';
import router from 'next/router';
import { Icon } from 'common/icon';

interface navItemProps {
  item: INavItem;
  variantBg?: 'notransparent';
  variantPadding?: 'small' | 'medium' | 'big';
  className?: string;
  arrowOpacity?: string;
  arrow?: boolean;
  asStatic?: boolean;
}

export const NavItem = ({
  item,
  variantBg,
  variantPadding,
  className,
  arrowOpacity = '0.4',
  arrow = false,
  asStatic = false,
}: navItemProps) => {
  const variantBgClass = useMemo(() => {
    switch (variantBg) {
      case 'notransparent':
        return styles.item_notransparent;
      default:
        return undefined;
    }
  }, [variantBg]);
  const variantPaddingClass = useMemo(() => {
    switch (variantPadding) {
      case 'small':
        return styles.item_small_padding;
      case 'medium':
        return styles.item_medium_padding;
      case 'big':
        return styles.item_big_padding;
      default:
        return undefined;
    }
  }, [variantPadding]);

  const classNames = clsx(
    styles.item_navbar,
    variantBgClass,
    variantPaddingClass,
    className
  );

  const content = (
    <>
      {item.icon}
      <span className={styles.text}>{item.text}</span>
      {arrow && (
        <Icon.Arrow width={20} height={20} fillOpacity={arrowOpacity} />
      )}
    </>
  );

  if (asStatic) {
    return <div className={classNames}>{content}</div>;
  }

  return (
    <Link href={item.anchor} className={classNames}>
      {content}
    </Link>
  );
};

export const NavigationBar = ({
  items,
  variantBg,
  variantPadding,
  navItemClassName,
  arrow,
  arrowOpacity,
}: {
  items: INavItem[];
  variantBg?: 'notransparent';
  variantPadding?: 'small' | 'medium' | 'big';
  navItemClassName?: string;
  arrow?: boolean;
  arrowOpacity?: string;
}) => {
  return (
    <nav className={styles.container}>
      {items.map((item, index) => (
        <NavItem
          item={item}
          key={index}
          variantBg={variantBg}
          variantPadding={variantPadding}
          className={navItemClassName}
          arrow={arrow}
          arrowOpacity={arrowOpacity}
        />
      ))}
    </nav>
  );
};

const TagFilterItem = ({
  item,
  isActive,
  handleClick,
  dropdownVisible,
  variant,
}: {
  item: ITagFilterItem;
  isActive: boolean;
  handleClick: () => void;
  dropdownVisible?: boolean;
  variant: 'transparent' | 'notransparent';
}) => {
  const itemClass = useMemo(() => {
    switch (variant) {
      case 'transparent':
        return styles.item_transparent;
      case 'notransparent':
        return styles.item_notransparent;
      default:
        return undefined;
    }
  }, [variant]);
  return (
    <button
      onClick={handleClick}
      className={clsx(
        styles.item_tagfilter,
        isActive && styles.active,
        itemClass
      )}
    >
      {item.icon}
      <div className={styles.text}>{item.text}</div>
      {item.type === 'more' && !dropdownVisible && (
        <div className={styles.arrowContainer}>
          <Icon.ArrowDown />
        </div>
      )}
      {item.type === 'more' && dropdownVisible && (
        <div className={styles.arrowContainer}>
          <Icon.ArrowDown direction="up" />
        </div>
      )}
      {item.type === 'navigate' && (
        <div className={styles.arrowContainer}>
          <Icon.Arrow width={20} height={20} />
        </div>
      )}
    </button>
  );
};

export const TagsFilters = ({
  items,
  variant,
  selectedOrdinaryFilters,
  setSelectedOrdinaryFilters,
  setFieldValue,
}: {
  items: ITagFilterItem[];
  variant: 'transparent' | 'notransparent';
  selectedOrdinaryFilters: SelectedOrdinaryFilters | string;
  setSelectedOrdinaryFilters: React.Dispatch<
    React.SetStateAction<SelectedOrdinaryFilters | string>
  >;
  setFieldValue: (field: string, value: any) => void;
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const handleClick = useCallback(
    (id: string) => {
      setSelectedOrdinaryFilters((prevFilters) => {
        if (typeof prevFilters === 'string') {
          return { [id]: true } as unknown as SelectedOrdinaryFilters;
        }

        const isFilterActive = prevFilters[id as keyof SelectedOrdinaryFilters];

        const newFilters: SelectedOrdinaryFilters = Object.fromEntries(
          Object.keys(prevFilters).map((key) => [key, false])
        ) as unknown as SelectedOrdinaryFilters;

        newFilters[id as keyof SelectedOrdinaryFilters] = !isFilterActive;

        Object.keys(prevFilters).forEach((key) => {
          setFieldValue(key, false);
        });
        setFieldValue(id, !isFilterActive);

        return newFilters;
      });
    },
    [setFieldValue, setSelectedOrdinaryFilters]
  );

  const handleMoreClick = useCallback(() => {
    setDropdownVisible((prev) => !prev);
  }, []);

  return (
    <nav className={styles.container}>
      {items.map((item, index) => {
        const isActive =
          typeof selectedOrdinaryFilters === 'string'
            ? selectedOrdinaryFilters === item.id
            : selectedOrdinaryFilters[item.id as keyof SelectedOrdinaryFilters];

        const handleItemClick = () => {
          switch (item.type) {
            case 'navigate':
              router.push(item.destination as string);
              return;
            case 'more':
              handleMoreClick();
              return;
            default:
              handleClick(item.id);
              return;
          }
        };

        return item.type === 'more' ? (
          <div key={index} className={styles.dropdownContainer}>
            <TagFilterItem
              dropdownVisible={dropdownVisible}
              item={item}
              isActive={isActive}
              handleClick={handleItemClick}
              variant={variant}
            />
            {dropdownVisible && (
              <div className={styles.dropdown}>
                {item.subItems?.map((subItem, subIndex) => {
                  const handleSubItemClick = () => {
                    switch (subItem.type) {
                      case 'navigate':
                        router.push(subItem.destination as string);
                        return;
                      case 'more':
                        handleMoreClick();
                        return;
                      default:
                        handleClick(subItem.id);
                        return;
                    }
                  };

                  return (
                    <TagFilterItem
                      dropdownVisible={dropdownVisible}
                      key={subIndex}
                      item={subItem}
                      isActive={
                        typeof selectedOrdinaryFilters === 'string'
                          ? selectedOrdinaryFilters === subItem.id
                          : selectedOrdinaryFilters[
                              subItem.id as keyof SelectedOrdinaryFilters
                            ]
                      }
                      handleClick={handleSubItemClick}
                      variant={variant}
                    />
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <TagFilterItem
            key={index}
            item={item}
            isActive={isActive}
            handleClick={handleItemClick}
            variant={variant}
          />
        );
      })}
    </nav>
  );
};
